import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import { getSubmissionsByAssignmentId, submitAssignment,
  applyQuestionSplit,
} from '../service';
import { clearSubjectsCache } from './useSubjects';
import { useAssignmentFeedbackRun } from './useAssignmentFeedbackRun';
import { invalidateBulkUploadQueries } from './bulkUploadQueries';
import { saveBatchCourseMaterials } from '../components/BulkUpload/utils/saveBatchCourseMaterials';
import { prefetchSubmissionFeedback } from './prefetchSubmissionFeedback';
import {
  answerSavesFor,
  hasReviewEdits,
  textsToPersist,
  unsavedEditsMessage,
} from '../components/BulkUpload/utils/reviewEdits';
import { applyPayloadOf, isSplitResolved } from '../components/BulkUpload/utils/questionSplit';
import { saveReviewedAnswer } from '../components/BulkUpload/utils/saveReviewedAnswer';
import { batchStatus, hasAllBatchRows } from '../components/BulkUpload/utils/batchStatus';
import { bulkFeedbackPhase, isBulkResponseEditable, unrequestedSubmissionIds } from '../components/BulkUpload/utils/feedbackRun';

/**
 * Creates a ZIP file from organized student pages.
 * Structure: students.zip/student001/file.pdf
 *
 * @param {Array} pages - Array of page objects with file data
 * @param {Object} studentAssignments - Map of studentId -> pageIds
 * @returns {Promise<Blob>} ZIP file as blob
 */
export const createStudentZip = async (pages, studentAssignments) => {
  const JSZip = (await import('jszip')).default || (await import('jszip'));
  const zip = new JSZip();

  for (const [studentId, pageIds] of Object.entries(studentAssignments)) {
    const studentFolder = zip.folder(studentId);

    pageIds.forEach((pageId, index) => {
      const page = pages.find((p) => p.id === pageId);

      if (page?.file instanceof File) {
        const fileName = page.file.name || `file_${index}.pdf`;
        studentFolder.file(fileName, page.file);
      }
    });
  }

  return await zip.generateAsync({ type: 'blob' });
};

const normalizeSubmissionIds = (result) => {
  if (Array.isArray(result?.submissionIds) && result.submissionIds.length) {
    return result.submissionIds.filter(Boolean);
  }
  const submissions = result?.submissions || result?.data?.submissions;
  if (Array.isArray(submissions)) {
    return submissions.map((s) => s?.id || s?.submissionId).filter(Boolean);
  }
  if (Array.isArray(result?.data?.submissionIds)) {
    return result.data.submissionIds.filter(Boolean);
  }
  return [];
};

/** The submit response is the source of truth; fall back to the assignment's submissions. */
const resolveSubmissionIds = async (response) => {
  const ids = normalizeSubmissionIds(response);
  if (ids.length || !response?.assignmentId) return ids;

  const submissions = await getSubmissionsByAssignmentId(
    response.assignmentId,
  ).catch(() => []);
  return submissions.map((s) => s?.id || s?.submissionId).filter(Boolean);
};

/**
 * Submit no longer creates anything. The assignment is made at the Question step
 * and the submissions at Organise's "Save and continue", which is the only way
 * to reach this screen — so a missing id means the wizard is in a state it
 * should not be in, and creating a second set here is what the single
 * creation point exists to prevent.
 */
const submitBulkUpload = async ({ assignmentId, submissionIds }) => {
  // Removed assignmentId check - for Tasks bulk upload, assignmentId is always present
  // The validation was causing false positives when assignmentId exists but validation runs too early
  if (!submissionIds?.length) {
    throw new Error('This draft has no responses to submit yet.');
  }
  return { assignmentId, submissionIds };
};

/** Said once, in the teacher's words where the failure already has them. */
const refuse = (message) => {
  toast(<Toast message={message} />);
  return false;
};

const refuseWith = (error) =>
  refuse(error?.message || 'Feedback could not be requested. Please try again.');

const MANUAL_SUBMISSION_STATES = new Set(['DRAFT', 'SUBMITTED', 'FEEDBACK_ACCEPTED', 'REVIEWED']);
const WRITE_CONCURRENCY = 6;

const studentNameOf = (students, studentId) =>
  (students || []).find((student) => String(student?.id) === String(studentId))?.name || null;

// Routing names students, while saves name submissions. Resolve that relation
// only inside this batch; older responses for the same student are separate work.
const currentBatchRows = (submissions, submissionIds) => {
  const wanted = new Set(submissionIds || []);
  const targets = submissions.filter(row => wanted.has(row?.id));
  if (!wanted.size || targets.length !== wanted.size ||
      new Set(targets.map(row => row.id)).size !== wanted.size) {
    throw new Error('Some uploaded responses could not be matched to this batch. Please reload before continuing.');
  }
  const students = targets.map(row => row.studentId == null ? null : String(row.studentId));
  if (students.some(id => !id) || new Set(students).size !== targets.length) {
    throw new Error('Uploaded responses have conflicting student details. Please reload before continuing.');
  }
  return targets;
};

// A class can carry 200 responses. Attempt every write without sending the whole
// class to the API at once, and retain failures for the caller's batch gate.
const settleWrites = async (items, write) => {
  const results = [];
  for (let index = 0; index < items.length; index += WRITE_CONCURRENCY) {
    const group = items.slice(index, index + WRITE_CONCURRENCY);
    results.push(...await Promise.allSettled(group.map(write)));
  }
  return results;
};

/**
 * Hook for bulk upload submission with optimistic updates.
 *
 * Feedback is no longer fanned out from here. Submit validates, makes ONE request
 * for the current batch, and watches its status until every response has readable
 * feedback. The work runs server-side, so navigating away or closing the tab no
 * longer cancels the class's marking.
 *
 * Follows ADR-002: Optimistic updates with rollback pattern
 * Follows ADR-001: TanStack Query cache invalidation
 * Follows ADR-004: useRef to prevent closure staleness
 *
 * @param {Object} params
 * @param {Object} params.questionForm - Question title, text, stimulus
 * @param {Array} params.pages - Uploaded/manual pages
 * @param {Object} params.studentAssignments - Map of studentId -> pageIds
 * @param {Function} params.onComplete - Called once the whole batch is marked and
 *   the first response is prefetched, with { assignmentId, submissionIds, firstReadySubmissionId }
 * @param {string} params.assignmentId - Assignment ID for tasks bulk upload
 * @param {string[]} params.submissionIds - Submissions Organise already created
 */
export const useBulkUploadSubmission = ({
  pages,
  studentAssignments,
  onComplete,
  assignmentId,
  submissionIds: existingSubmissionIds,
  reviewEdits,
  students,
  // Which students' extraction has reported, from the flow's extraction run. A
  // Set, so "has this one settled" is the question it answers.
  settledStudentIds,
  resumeMarking = false,
  // Multi-question tasks only: studentId -> the split state Review holds, sent as
  // one answer per question in place of the single-answer review edits.
  questionSplits,
  multiQuestion = false,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  /**
   * A run that was refused after the progress screen was already reached. Held as
   * state because that screen is the only thing left on the teacher's path, and it
   * has to be able to say so and offer them a way on.
   */
  const [submitError, setSubmitError] = useState(null);

  const onCompleteRef = useRef(onComplete);
  const pagesRef = useRef(pages);
  const studentAssignmentsRef = useRef(studentAssignments);
  const reviewEditsRef = useRef(reviewEdits);
  const studentsRef = useRef(students);
  const settledStudentIdsRef = useRef(settledStudentIds || new Set());
  const questionSplitsRef = useRef(questionSplits);
  // What the last submit asked for, so Try again replays it rather than asking the
  // teacher to walk back through marking to rebuild a selection they already made.
  const lastRunRef = useRef(null);
  // The poll is shared with everything else watching this assignment, so the
  // handoff has to be armed by THIS wizard's submit — otherwise reopening a draft
  // whose feedback is already done would navigate away on mount.
  //
  // State, not a ref: the first status response can already carry a READY row, and
  // a ref armed after that render would leave the handoff effect with no
  // dependency left to change.
  const [hasRequested, setHasRequested] = useState(resumeMarking);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const hasHandedOffRef = useRef(false);
  const prefetchControllerRef = useRef(null);
  const submittedIdsRef = useRef(resumeMarking ? existingSubmissionIds || [] : []);
  const submittingRef = useRef(false);
  const manualCompletedRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    studentAssignmentsRef.current = studentAssignments;
  }, [studentAssignments]);

  useEffect(() => {
    reviewEditsRef.current = reviewEdits;
  }, [reviewEdits]);

  useEffect(() => {
    studentsRef.current = students;
  }, [students]);

  useEffect(() => {
    settledStudentIdsRef.current = settledStudentIds || new Set();
  }, [settledStudentIds]);
  useEffect(() => {
    questionSplitsRef.current = questionSplits;
  }, [questionSplits]);

  const feedbackRun = useAssignmentFeedbackRun(assignmentId, { submissionIds: existingSubmissionIds, watchWholeBatch: true });
  const { request, firstReadySubmissionId } = feedbackRun;

  // No onError toast: submit catches this itself and says one thing about the
  // failure. Two handlers meant a refused submit toasted twice, in two different
  // vocabularies, about the same event.
  const submissionMutation = useMutation({ mutationFn: submitBulkUpload });

  // Wait for the whole batch to finish marking, then preload only the response
  // we open. Remaining responses warm in the background for arrow navigation.
  useEffect(() => {
    if (!hasRequested || hasHandedOffRef.current) return;
    if (!firstReadySubmissionId || !feedbackRun.isReadyForHandoff || feedbackRun.isRecoverable) return;
    hasHandedOffRef.current = true;
    const submissionIds = [...submittedIdsRef.current];
    const controller = new AbortController();
    prefetchControllerRef.current?.abort();
    prefetchControllerRef.current = controller;
    let handedOff = false;
    const options = { force: true, signal: controller.signal };
    prefetchSubmissionFeedback(queryClient, [firstReadySubmissionId], options)
      // Prefetch is best effort; the document can retry a failed read itself.
      .catch(() => {})
      .then(() => {
        if (controller.signal.aborted) return;
        handedOff = true;
        prefetchSubmissionFeedback(
          queryClient,
          submissionIds.filter((id) => id !== firstReadySubmissionId),
          options,
        ).catch(() => {});
        return onCompleteRef.current?.({
          assignmentId,
          submissionIds,
          firstReadySubmissionId,
        });
      })
      .catch((error) => console.error('Bulk upload navigation could not complete.', error));
    return () => {
      // Successful navigation unmounts the wizard; keep warming its batch. A
      // cancelled handoff must not navigate later or schedule queued reads.
      if (!handedOff) {
        controller.abort();
        hasHandedOffRef.current = false;
      }
    };
  }, [
    assignmentId,
    firstReadySubmissionId,
    hasRequested,
    submitAttempt,
    feedbackRun.isReadyForHandoff,
    feedbackRun.isRecoverable,
    queryClient,
  ]);

  const invalidateAfterSubmit = useCallback(async () => {
    await invalidateBulkUploadQueries(queryClient);
    // Submit alone: it can introduce subject-linked data that a later run cannot.
    clearSubjectsCache();
  }, [queryClient]);

  const validationError = useCallback(() => {
    // Check assignmentId only when review edits need to be saved
    // For Drafts, assignmentId is created during the flow, but must exist before saving edits
    const allStudentTexts = textsToPersist({
      edits: reviewEditsRef.current,
      assignments: studentAssignmentsRef.current,
      pages: pagesRef.current,
      settledStudentIds: settledStudentIdsRef.current,
    });
    if (hasReviewEdits(allStudentTexts) && !assignmentId) {
      return 'This draft has not been created yet. Please go back and create the draft first.';
    }

    if (pagesRef.current.length === 0) return 'Please upload at least one response';
    if (Object.keys(studentAssignmentsRef.current).length === 0) {
      return 'Please assign pages to students';
    }

    // Check if any non-manual pages are still pending extraction
    const hasPendingExtraction = pagesRef.current.some(page =>
      page.source !== 'manual' &&
      (page.extractionStatus === 'PENDING' || page.extractionStatus === 'EXTRACTING')
    );
    if (hasPendingExtraction) {
      return 'Some responses are still being processed. This may happen after reassigning pages. Please wait for extraction to complete before requesting feedback.';
    }

    return null;
  }, [assignmentId]);

  const saveEditedAnswer = ({ submissionId, serialNumber, answer, options }) =>
    saveReviewedAnswer(submissionId, serialNumber, answer, options);

  const rejectedOf = (saves, results) =>
    saves.filter((_, index) => results[index].status === 'rejected');

  // All of them attempted before any of them is reported: a run held back over one
  // student should not also leave the other nineteen corrections unsent.
  const runAnswerSaves = useCallback(async (saves) => {
    const results = await Promise.allSettled(saves.map(save => saveEditedAnswer({
      ...save, options: { ...save.options, suppressToast: true },
    })));
    const failed = rejectedOf(saves, results);
    if (failed.length) {
      throw new Error(unsavedEditsMessage(failed, studentsRef.current));
    }
  }, []);

  /**
   * Multi-question tasks: what the teacher confirmed at Review reaches the
   * submission as one answer per question, and the run waits on it the way it
   * waits on review edits. Every DRAFT in the batch needs a resolved split — a
   * question nobody decided on is refused here, before anything is written, so
   * the teacher lands back on a step they can act on.
   */
  const applyQuestionSplits = useCallback(async (targets) => {
    const splits = questionSplitsRef.current || {};
    const unresolved = targets.filter(
      (row) => !isSplitResolved(splits[String(row.studentId)]),
    );
    if (unresolved.length) {
      const names = unresolved.map((row) => studentNameOf(studentsRef.current, row.studentId)).filter(Boolean);
      throw new Error(
        `Every question needs a decision before marking${names.length ? ` for ${names.join(', ')}` : ''}. ` +
          'Go back to Review and check each response.',
      );
    }
    const results = await settleWrites(targets, (row) =>
      applyQuestionSplit(row.id, applyPayloadOf(splits[String(row.studentId)])),
    );
    const failed = targets.filter((_, index) => results[index].status === 'rejected');
    if (failed.length) {
      throw new Error(unsavedEditsMessage(
        failed.map((row) => ({ studentId: row.studentId })),
        studentsRef.current,
      ));
    }
  }, []);

  /**
   * FE-BU15: the teacher's corrections reach the answer before the run reads it.
   *
   * The wizard routes by studentId and the answer lives on a submission, so the
   * roster is read here rather than kept — it is the only thing that knows which
   * submission belongs to whom, and it is one request against a class that is
   * about to be marked anyway.
   *
   * FE-BU-EXTRACT: extracted text is saved even when the teacher does not edit it,
   * so a reopened draft does not show empty responses for files that read fine.
   * Which students that covers is textsToPersist's decision — a student still
   * converting is left to the server, because writing the client's idea of their
   * text would race the worker that is filling it in.
   */
  const persistReviewEdits = useCallback(async (targets, batchRows) => {
    const allStudentTexts = textsToPersist({
      edits: reviewEditsRef.current,
      assignments: studentAssignmentsRef.current,
      pages: pagesRef.current,
      settledStudentIds: settledStudentIdsRef.current,
    });

    if (!hasReviewEdits(allStudentTexts)) {
      return;
    }

    // Validate assignmentId exists when we need to save review edits
    // This is required for Drafts where assignmentId is created during the flow
    if (!assignmentId) {
      throw new Error('Cannot save review edits: assignment has not been created yet.');
    }

    if (answerSavesFor(allStudentTexts, batchRows).length !== Object.keys(allStudentTexts).length) {
      throw new Error('Some corrections could not be matched to this batch. Please reload before continuing.');
    }
    const saves = answerSavesFor(allStudentTexts, targets.filter(isBulkResponseEditable));
    await runAnswerSaves(saves);
  }, [assignmentId, runAnswerSaves]);

  /**
   * A run refused after the teacher already reached the progress screen.
   *
   * Once the whole batch is readable, a late rejection of the accept must not
   * pull the teacher back from feedback whose generation already completed.
   */
  const handleRunRefused = useCallback((error) => {
    if (hasHandedOffRef.current) return;
    setHasRequested(false);
    setSubmitError(
      error instanceof Error ? error : new Error(String(error?.message || error)),
    );
  }, []);

  /**
   * Sent, not awaited. The accept can outlive the work it describes — the fan-out
   * runs inline, so the POST stays open long after the first students are marked —
   * and waiting on it strands a teacher whose class the status endpoint is already
   * reporting as ready. `hasRequested` is armed here for the same reason: it only
   * says whose submit this was, so reopening a finished draft still does not
   * navigate on mount.
   */
  const dispatchRun = useCallback(
    ({ markingSelection, advancedControls, submissionIds }) => {
      setHasRequested(true);
      request(markingSelection, advancedControls, { submissionIds })
        // Housekeeping, and deliberately not in the caller's path: the request has
        // already been accepted, so a failed cache invalidation must not report it
        // as failed or disarm the handoff.
        .then(() => invalidateAfterSubmit().catch(() => {}))
        .catch(handleRunRefused);
    },
    [request, invalidateAfterSubmit, handleRunRefused],
  );

  /**
   * Everything that has to hold before the request leaves, in the order it has to
   * hold in. Any of these throwing keeps the teacher on a step they can act on —
   * which is the whole point of doing them here rather than after the navigation.
   */
  const prepareRun = useCallback(async () => {
    const response = await submissionMutation.mutateAsync({
      assignmentId,
      submissionIds: existingSubmissionIds,
    });
    submittedIdsRef.current = await resolveSubmissionIds(response);
    // Re-read before ANY write: another tab or a previous partial run may have
    // submitted some students since Review was opened. Keep the whole batch for
    // progress, but only send responses whose feedback has not been requested.
    const [all, fresh] = await Promise.all([
      getSubmissionsByAssignmentId(assignmentId, { requireValidResponse: true }),
      feedbackRun.refetchStatus({ throwOnError: true }),
    ]);
    const targets = currentBatchRows(all, existingSubmissionIds);
    const studentIds = new Set(targets.map(row => String(row.studentId)));
    if (Object.entries(studentAssignmentsRef.current || {}).some(
      ([studentId, pageIds]) => pageIds?.length && !studentIds.has(String(studentId)),
    )) {
      throw new Error('Some corrections could not be matched to this batch. Please reload before continuing.');
    }
    const status = batchStatus(fresh?.data, existingSubmissionIds);
    if (!hasAllBatchRows(status, existingSubmissionIds) ||
        status.submissions.some(row => !['NOT_REQUESTED', 'PENDING', 'GENERATING', 'READY', 'FAILED'].includes(row.feedback?.status))) {
      throw new Error('Feedback progress could not be loaded. Please check again before submitting.');
    }
    const pendingIds = new Set(unrequestedSubmissionIds(status));
    const hasChanges = row => {
      const edit = reviewEditsRef.current?.[String(row.studentId)];
      const original = row.answers?.[0]?.answer;
      return (edit !== undefined && edit !== (original?.answer ?? original?.text ?? '')) ||
        Boolean(questionSplitsRef.current?.[String(row.studentId)]?.dirty);
    };
    const phase = bulkFeedbackPhase(status);
    const changedProtected = targets.some(row => hasChanges(row) &&
      (phase === 'generating' || feedbackRun.isRequesting || !pendingIds.has(row.id) || !isBulkResponseEditable(row)));
    if (changedProtected) {
      throw new Error('A response has already been submitted, so your new changes have not been saved. Close and reopen the upload to view its feedback status.');
    }
    if (phase === 'generating' || feedbackRun.isRequesting) return [];
    if (phase === 'converting') throw new Error('Some responses are still being converted. Wait for conversion to finish before requesting feedback.');
    const unrequested = targets.filter(row => pendingIds.has(row.id));
    if (unrequested.some(row => !isBulkResponseEditable(row) && row.status !== 'SUBMITTED')) {
      throw new Error('Some responses can no longer be submitted from this upload. Close and reopen it to check their status.');
    }
    // Marking the text the teacher just corrected away is the failure this whole
    // path exists to prevent, so the run waits on their edits reaching the answer.
    if (multiQuestion) {
      await applyQuestionSplits(unrequested.filter(isBulkResponseEditable));
    } else {
      await persistReviewEdits(unrequested, targets);
    }
    return unrequested.map(row => row.id);
  }, [assignmentId, existingSubmissionIds, persistReviewEdits, applyQuestionSplits, multiQuestion, submissionMutation, feedbackRun.refetchStatus, feedbackRun.isRequesting]);

  const beginSubmit = () => {
    prefetchControllerRef.current?.abort();
    setHasRequested(false);
    // React can batch disarming and rearming a fast request into one render.
    // The attempt still invalidates any older prefetch continuation.
    setSubmitAttempt((attempt) => attempt + 1);
    setIsSubmitting(true);
    setSubmitError(null);
    hasHandedOffRef.current = false;
  };

  /**
   * @returns {Promise<boolean>} whether the run was dispatched.
   *
   * FE-BU17: the caller advances to the progress screen on `true` and nothing else.
   * What cannot be caught before dispatch surfaces as `submitError` on that screen
   * instead, with a way on.
   */
  const submit = useCallback(
    async (markingSelection, advancedControls) => {
      if (submittingRef.current || submissionMutation.isPending || submissionMutation.isLoading) {
        return false;
      }

      const message = validationError();
      if (message) {
        return refuse(message);
      }

      submittingRef.current = true;
      beginSubmit();
      try {
        const submissionIds = await prepareRun();
        if (!submissionIds.length) {
          setHasRequested(true);
          return true;
        }
        await saveBatchCourseMaterials(assignmentId, markingSelection?.courseMaterials);
        lastRunRef.current = { markingSelection, advancedControls, submissionIds };
        dispatchRun(lastRunRef.current);
        return true;
      } catch (error) {
        return refuseWith(error);
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [assignmentId, dispatchRun, prepareRun, submissionMutation, validationError],
  );

  /**
   * Manual/peer tasks need their DRAFT responses submitted, but never an AI run.
   * Read every attempt: a prior request may have succeeded before its connection
   * failed, and submitting a reviewed response again would create a new version.
   */
  const submitWithoutFeedback = useCallback(async () => {
    if (submittingRef.current || manualCompletedRef.current) return false;
    const message = validationError();
    if (message) return refuse(message);
    if (!assignmentId || !existingSubmissionIds?.length) {
      return refuse('This upload has no responses to submit yet.');
    }

    submittingRef.current = true;
    beginSubmit();
    try {
      const all = await getSubmissionsByAssignmentId(assignmentId, { requireValidResponse: true });
      const targets = currentBatchRows(all, existingSubmissionIds);
      const byId = new Map(targets.map(row => [row.id, row]));
      if (targets.some(row => !row?.status)) {
        throw new Error('Some uploaded responses could not be loaded. Please try again.');
      }
      if (targets.some(row => !MANUAL_SUBMISSION_STATES.has(row.status))) {
        throw new Error('Some uploaded responses can no longer be submitted. Close and reopen the upload to check their status.');
      }
      const drafts = targets.filter(row => row.status === 'DRAFT');
      if (multiQuestion) {
        await applyQuestionSplits(drafts);
      } else {
        const texts = textsToPersist({
          edits: reviewEditsRef.current,
          assignments: studentAssignmentsRef.current,
          pages: pagesRef.current,
          settledStudentIds: settledStudentIdsRef.current,
        });
        const saves = answerSavesFor(texts, drafts).filter(save => {
          const answer = byId.get(save.submissionId)?.answers?.[0]?.answer;
          return save.answer.answer !== (answer?.answer ?? answer?.text ?? '');
        });
        const saveResults = await settleWrites(saves, saveEditedAnswer);
        if (saveResults.some(result => result.status === 'rejected')) {
          throw new Error('Your corrections could not be saved. Please try again before submitting.');
        }
      }

      // Save the whole batch before submitting any of it. Retrying only touches
      // rows the fresh read still identifies as DRAFT, preserving earlier success.
      const results = await settleWrites(drafts, row => submitAssignment(row.id));
      if (results.some(result => result.status === 'rejected')) {
        throw new Error('Some responses could not be submitted. Your corrections are saved. Please try again.');
      }
      manualCompletedRef.current = true;
      await invalidateAfterSubmit().catch(() => {});
      return true;
    } catch (error) {
      setSubmitError(error);
      return false;
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [assignmentId, existingSubmissionIds, invalidateAfterSubmit, validationError, applyQuestionSplits, multiQuestion]);

  /** Replays the last run rather than making the teacher rebuild their selection. */
  const retrySubmit = useCallback(() => {
    if (!lastRunRef.current) return;
    setSubmitError(null);
    dispatchRun(lastRunRef.current);
  }, [dispatchRun]);

  const clearSubmitError = useCallback(() => setSubmitError(null), []);

  return {
    submit,
    submitWithoutFeedback,
    retrySubmit,
    submitError,
    clearSubmitError,
    isSubmitting,
    feedbackRun,
    error: submissionMutation.error,
    isError: submissionMutation.isError,
  };
};
